import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';

const Hero = () => {
    const [cursorVisible, setCursorVisible] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => setCursorVisible(v => !v), 530);
        return () => clearInterval(interval);
    }, []);

    return (
        <section className="relative pt-16 sm:pt-24 pb-20 sm:pb-32 overflow-hidden">
            {/* Background effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-cyan-500/5 blur-[120px] rounded-full" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/5 blur-[120px] rounded-full" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                    {/* Left side - Content */}
                    <div className="flex-1 text-center lg:text-left">
                        {/* Terminal badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8">
                            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                            <span className="font-mono text-xs text-cyan-400">API Testing Platform</span>
                        </div>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight mb-6 sm:mb-8 leading-[1.1]">
                            <span className="font-mono text-gray-500 text-lg sm:text-xl block mb-3">{'>'} imago.init()</span>
                            API Testing & Development Platform{' '}
                            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">Built for Modern Teams</span>
                        </h1>

                        <p className="max-w-xl text-base sm:text-lg text-gray-400 mb-10 sm:mb-12 leading-relaxed mx-auto lg:mx-0">
                            From API testing to complete development workflows. Imago empowers your entire team—from business analysts to testers—with AI-powered automation and intelligent tooling.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 mb-12 sm:mb-16 w-full sm:w-auto justify-center lg:justify-start">
                            <Link to="/sign-up" className="group inline-flex items-center justify-center px-8 py-4 text-base font-bold font-mono rounded-lg text-black bg-gradient-to-r from-cyan-400 to-cyan-300 hover:from-cyan-300 hover:to-cyan-200 transition-all shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40">
                                <span className="text-cyan-700 mr-2 group-hover:translate-x-0.5 transition-transform">{'>'}</span>
                                Get Started
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <button className="group inline-flex items-center justify-center px-8 py-4 border border-white/10 text-base font-bold font-mono rounded-lg text-gray-300 bg-white/5 hover:bg-white/10 hover:border-cyan-500/30 transition-all backdrop-blur-sm">
                                <Play className="mr-2 h-5 w-5 fill-current text-cyan-400" />
                                Watch Demo
                            </button>
                        </div>
                    </div>

                    {/* Right side - Code block (like sahilsudan.com) */}
                    <div className="flex-1 w-full max-w-lg">
                        <div className="bg-[#111111] rounded-xl border border-white/10 shadow-2xl shadow-cyan-500/5 overflow-hidden">
                            {/* Window chrome */}
                            <div className="flex items-center gap-2 px-4 py-3 bg-[#1a1a1a] border-b border-white/5">
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                                    <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                                    <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                                </div>
                                <span className="ml-3 font-mono text-xs text-gray-500">imago-platform.ts</span>
                            </div>
                            {/* Code content */}
                            <div className="p-5 sm:p-6 font-mono text-sm leading-relaxed">
                                <div className="text-purple-400">const <span className="text-cyan-300">platform</span> <span className="text-white">=</span> {'{'}</div>
                                <div className="pl-4 sm:pl-6">
                                    <div><span className="text-gray-500">name:</span> <span className="text-[#a5d6ff]">"Imago"</span><span className="text-gray-600">,</span></div>
                                    <div><span className="text-gray-500">type:</span> <span className="text-[#a5d6ff]">"API Testing Platform"</span><span className="text-gray-600">,</span></div>
                                    <div><span className="text-gray-500">company:</span> <span className="text-[#a5d6ff]">"Spense"</span><span className="text-gray-600">,</span></div>
                                    <div><span className="text-gray-500">features:</span> <span className="text-white">[</span></div>
                                    <div className="pl-4 sm:pl-6">
                                        <div><span className="text-[#a5d6ff]">"API Testing"</span><span className="text-gray-600">,</span></div>
                                        <div><span className="text-[#a5d6ff]">"Orchestration"</span><span className="text-gray-600">,</span></div>
                                        <div><span className="text-[#a5d6ff]">"Load Testing"</span><span className="text-gray-600">,</span></div>
                                        <div><span className="text-[#a5d6ff]">"AI Automation"</span></div>
                                    </div>
                                    <div><span className="text-white">]</span><span className="text-gray-600">,</span></div>
                                    <div><span className="text-gray-500">stack:</span> <span className="text-white">[</span><span className="text-[#a5d6ff]">"React"</span><span className="text-gray-600">,</span> <span className="text-[#a5d6ff]">"Node.js"</span><span className="text-gray-600">,</span> <span className="text-[#a5d6ff]">"AI"</span><span className="text-white">]</span><span className="text-gray-600">,</span></div>
                                    <div><span className="text-gray-500">status:</span> <span className="text-[#a5d6ff]">"Production Ready"</span></div>
                                </div>
                                <div className="text-white">{'}'}<span className="text-gray-600">;</span><span className={`ml-0.5 inline-block w-2 h-4 bg-cyan-400 align-middle ${cursorVisible ? 'opacity-100' : 'opacity-0'}`} /></div>
                            </div>
                        </div>

                        {/* Floating badges like sahilsudan.com */}
                        <div className="flex flex-wrap gap-2 mt-4 justify-center lg:justify-start">
                            {['⚡ API Testing', '🔗 Orchestration', '🤖 AI-Powered'].map((badge) => (
                                <span key={badge} className="px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono text-cyan-400">
                                    {badge}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Hero;
